import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
} from "@mui/material";
import CustomAppBar from "../../components/AppBar";
import api from "../../globals/axios";

const AreaManagerTargets = () => {
  const { id } = useParams();
  const [areaManager, setAreaManager] = useState(null);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    formatDateForInput(new Date())
  );
  const [formData, setFormData] = useState({
    farmer_target: "",
    area_target: "",
  });

  // Helper function to format date for input field
  function formatDateForInput(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Helper function to format date for display
  function formatDateForDisplay(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  const fetchTargets = async () => {
    try {
      const response = await api.get(
        `/popmanager/set-area-manager-target/?area_manager_id=${id}`
      );
      setTargets(response.data.data.targets);
      setAreaManager({
        name: response.data.data.area_manager_details.full_name,
        address: response.data.data.area_manager_details.address,
        id: response.data.data.area_manager_details.id,
      });
    } catch (error) {
      setError(error.response?.data?.error || "Failed to fetch targets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTargets();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post("/popmanager/set-area-manager-target/", {
        area_manager_id: parseInt(id),
        target_date: selectedDate,
        farmer_target: parseInt(formData.farmer_target),
        area_target: parseInt(formData.area_target),
      });

      setTargets([...targets, response.data.data]);
      setFormData({ farmer_target: "", area_target: "" });
      setSelectedDate(formatDateForInput(new Date()));
    } catch (error) {
      setError(error.response?.data?.error || "Failed to set target");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xs" style={{ marginTop: 20 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Area Manager Summary Cards */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Area Manager Name: {areaManager?.name || "-"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              District: {areaManager?.address || "-"}
            </Typography>
          </Grid>

          {/* Set Target Form */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Set New Target
              </Typography>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Target Date"
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Farmer Target"
                      type="number"
                      name="farmer_target"
                      value={formData.farmer_target}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          farmer_target: e.target.value,
                        })
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Area Target"
                      type="number"
                      name="area_target"
                      value={formData.area_target}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          area_target: e.target.value,
                        })
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      Set Target
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          {/* Targets History */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Target History
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Farmer Target</TableCell>
                    <TableCell align="right">Area Target</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {targets.map((target) => (
                    <TableRow key={target.id}>
                      <TableCell>
                        {formatDateForDisplay(target.target_date)}
                      </TableCell>
                      <TableCell align="right">
                        {target.farmer_target}
                      </TableCell>
                      <TableCell align="right">{target.area_target}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default AreaManagerTargets;
